import { OrganizedUserGroups, UserGroup } from "@/app/types/sale-log-types";
import { User } from "@/app/types/sale-log-types";
import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const getBusinessDaysPassed = (): number => {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth() + 1; // Months in JavaScript are 0-indexed
  const yearMonth = `${year}-${month.toString().padStart(2, "0")}-01T00:00:00`;

  const first = new Date(yearMonth);
  const today = new Date();

  let days = 0;

  while (first < today) {
    if (first.getDay() !== 0) {
      // Sunday is represented by 0 in JavaScript Date objects
      days++;
    }
    first.setDate(first.getDate() + 1);
  }

  return days;
};

export const getTotalBusinessDays = (): number => {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);

  let days = 0;

  for (
    let currentDay = firstDay;
    currentDay <= lastDay;
    currentDay.setDate(currentDay.getDate() + 1)
  ) {
    if (currentDay.getDay() !== 0) {
      // Sunday is represented by 0 in JavaScript Date objects
      days++;
    }
  }

  return days;
};

// Returns true if date is week day
function isWeekday(date: Date) {
  const day = date.getDay();
  return day != 0;
}

// Get dates of all work days in one month
export function getWeekdaysInMonth(month: number, year: number) {
  return getDaysInMonth(month, year).filter(isWeekday);
}

// Get dates of all days in one month
export function getDaysInMonth(month: number, year: number) {
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);

  return getDaysInRange(firstDay, lastDay);
}

// Get dates between 2 dates
function getDaysInRange(start: Date, end: Date) {
  const r: Date[] = [];
  for (let dt = start; dt <= end; dt.setDate(dt.getDate() + 1)) {
    r.push(new Date(dt));
  }
  return r;
}

export function getSundaysElapsed() {
  const sundays: Date[] = [];

  const firstDay = new Date(new Date().getMonth(), new Date().getFullYear(), 1);
  const currentDateOfMonth = new Date();

  if (firstDay.getDay() == 6) {
    if (currentDateOfMonth > firstDay) {
      sundays.push(firstDay);
    }
  } else {
    while (firstDay.getDay() != 6) {
      firstDay.setDate(firstDay.getDate() + 1);
    }
  }

  for (let i = 0; i < 4; i++) {
    sundays.push(firstDay);

    if (firstDay > currentDateOfMonth) return sundays;

    firstDay.setDate(firstDay.getDate() + 7);
  }

  return sundays;
}

export const sortUsersIntoDynamicGroups = (
  dealershipId: string,
  houseDealsEnabled: boolean = false,
  users: Array<User>,
  userGroups: Array<UserGroup> = [],
): OrganizedUserGroups => {
  if (!dealershipId)
    return userGroups.reduce((acc, group) => {
      acc[group.name] = [];
      return acc;
    }, {});

  const organizedUserGroups = {};
  const userGroupNameMap = userGroups.reduce((map, group) => {
    map[group._id] = group.name;
    return map;
  }, {});

  users.forEach((user) => {
    if (user.userGroupIds[dealershipId]) {
      user.userGroupIds[dealershipId].forEach((id) => {
        const groupName = userGroupNameMap[id];
        if (groupName) {
          if (!organizedUserGroups[groupName]) {
            organizedUserGroups[groupName] = [];
          }
          organizedUserGroups[groupName].push(user);
        }
      });
    }
  });

  if (houseDealsEnabled) {
    Object.keys(organizedUserGroups).forEach((key) => {
      organizedUserGroups[key].push({
        name: "House Deals",
      });
    });
  }

  return organizedUserGroups;
};

export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(amount);
}

export function generateColorPalette(count: number): string[] {
  const baseColors = [
    "#00d97e", // CarTracked Green
    "#000000", // Black
    "#6b7280", // Gray
    "#3b82f6", // Blue
    "#8b5cf6", // Purple
    "#ec4899", // Pink
    "#f59e0b", // Amber
    "#10b981", // Emerald
    "#06b6d4", // Cyan
    "#f97316", // Orange
    "#6366f1", // Indigo
    "#14b8a6", // Teal
  ];

  if (count <= baseColors.length) {
    return baseColors.slice(0, count);
  }

  // Generate additional colors using HSL
  const colors = [...baseColors];
  const remaining = count - baseColors.length;

  for (let i = 0; i < remaining; i++) {
    const hue = (i * 137.5) % 360; // Golden angle for good distribution
    const saturation = 65 + (i % 3) * 10;
    const lightness = 45 + (i % 2) * 10;
    colors.push(`hsl(${hue}, ${saturation}%, ${lightness}%)`);
  }

  return colors;
}

/**
 * Format large numbers with K, M, B suffixes
 */
export function formatLargeNumber(num: number): string {
  if (num >= 1_000_000_000) {
    return (num / 1_000_000_000).toFixed(1).replace(/\.0$/, "") + "B";
  }
  if (num >= 1_000_000) {
    return (num / 1_000_000).toFixed(1).replace(/\.0$/, "") + "M";
  }
  if (num >= 1_000) {
    return (num / 1_000).toFixed(1).replace(/\.0$/, "") + "K";
  }
  return num.toString();
}

/**
 * Calculate optimal Y-axis max for charts
 */
export function calculateYAxisMax(values: number[]): number {
  const max = Math.max(...values);
  const magnitude = Math.pow(10, Math.floor(Math.log10(max)));
  const normalized = max / magnitude;

  let roundedMax;
  if (normalized <= 1.2) roundedMax = 1.5;
  else if (normalized <= 2.5) roundedMax = 3;
  else if (normalized <= 5) roundedMax = 6;
  else roundedMax = 10;

  return roundedMax * magnitude;
}

/**
 * Get a color with alpha transparency
 */
export function colorWithAlpha(color: string, alpha: number): string {
  // Convert hex to rgba
  if (color.startsWith("#")) {
    const r = parseInt(color.slice(1, 3), 16);
    const g = parseInt(color.slice(3, 5), 16);
    const b = parseInt(color.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }

  // If already rgb/rgba, modify alpha
  if (color.startsWith("rgb")) {
    return color.replace(/[\d.]+\)$/g, `${alpha})`).replace("rgb", "rgba");
  }

  return color;
}
